import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, Send } from "lucide-react";

/**
 * "Celebrate a Life Services" panel — shown on the memorial detail page.
 * Visitors can request a service (grave visits, flower placement, etc.) —
 * the memorial owner + admins see incoming requests via the
 * `memorial_service_requests` collection.
 *
 * Props:
 *   memorialKey : slug or id
 *   user        : current auth user (may be null)
 */
export default function MemorialServicesPanel({ memorialKey, user }) {
  const [catalog, setCatalog] = useState([]);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [preferredDate, setPreferredDate] = useState("");
  const [contactEmail, setContactEmail] = useState(user?.email || "");
  const [contactPhone, setContactPhone] = useState(user?.phone || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get("/memorial-services/catalogue").then((r) => setCatalog(r.data?.services || [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) { toast.error("Please pick a service first."); return; }
    if (!user) { toast.error("Please sign in to request a service."); return; }
    setBusy(true);
    try {
      await http.post(`/memorials/${memorialKey}/service-requests`, {
        kind: selected,
        note: note || undefined,
        preferred_date: preferredDate || undefined,
        contact_email: contactEmail || undefined,
        contact_phone: contactPhone || undefined,
      });
      toast.success("Request sent — the family will be in touch.");
      setSelected(null);
      setNote("");
      setPreferredDate("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send request");
    } finally { setBusy(false); }
  };

  return (
    <div className="wofy-card" data-testid="mem-services-panel">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4" />
        <div className="text-[11px] font-mono uppercase tracking-wider">Celebrate a Life services</div>
      </div>
      <p className="text-[12px] mb-3" style={{ color: "var(--wofy-text-soft)" }}>
        Request practical support in memory of a loved one — grave visits, flower placement, memorial photography and more.
      </p>

      <div className="flex flex-wrap gap-1 mb-3">
        {catalog.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setSelected(selected === s.key ? null : s.key)}
            className={`text-[11px] px-2.5 py-1 rounded-full border ${selected === s.key ? "bg-black text-white border-black" : "hover:bg-black/5"}`}
            style={selected !== s.key ? { borderColor: "var(--wofy-border)" } : undefined}
            data-testid={`mem-service-${s.key}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {selected && (
        <form onSubmit={submit} className="rounded-xl border p-3 space-y-2" style={{ borderColor: "var(--wofy-border)" }}>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider">Note (optional)</label>
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} placeholder="Any special instructions…" data-testid="mem-service-note" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[11px] font-mono uppercase tracking-wider">Preferred date</span>
              <input type="date" value={preferredDate} onChange={(e) => setPreferredDate(e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} data-testid="mem-service-date" />
            </label>
            <label className="block">
              <span className="text-[11px] font-mono uppercase tracking-wider">Contact email</span>
              <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} data-testid="mem-service-email" />
            </label>
          </div>
          <button disabled={busy} className="wofy-btn-primary w-full text-sm" data-testid="mem-service-submit">
            {busy ? <><Loader2 className="w-3 h-3 animate-spin" /> Sending…</> : <><Send className="w-3 h-3" /> Send request</>}
          </button>
        </form>
      )}
    </div>
  );
}
